/**
 * Demo dev: ?avatar=1 — 6 ras chodzi w ubrankach.
 * ?anchors=1 — kalibrator kotwic (przeciąganie + eksport JSON).
 */
import Phaser from 'phaser';
import { DOG_BREED_IDS, DOGS, GAME_HEIGHT, GAME_WIDTH } from '@pieskowo/shared';
import { AnimatedDogAvatar, preloadAnchors } from './AnimatedDogAvatar.js';
import { emptyOutfitSlots, type OutfitSlots } from './parseOutfit.js';
import {
  ALL_POSES,
  type PoseName,
  parseAnchorsFile,
  resolveAnchors,
  buildDefaultAnchorsFile,
  type AnchorPose,
} from './anchors.js';

const DEMO_OUTFITS: OutfitSlots[] = [
  { ...emptyOutfitSlots(), body: 'dress_pink', hat: 'hat_sun', bow: 'bow_red' },
  { ...emptyOutfitSlots(), body: 'shirt_blue', collar: 'bandana_blue', glasses: 'glasses_round' },
  { ...emptyOutfitSlots(), body: 'sweater_green', hat: 'hat_crown', accessory: 'acc_star' },
  { ...emptyOutfitSlots(), body: 'dress_flowers', bow: 'bow_pink_big', shoes: 'shoes_pink' },
  { ...emptyOutfitSlots(), body: 'raincoat_yellow', hat: 'hat_winter', collar: 'collar_heart' },
  { ...emptyOutfitSlots(), body: 'tshirt_stripes', glasses: 'glasses_sun', accessory: 'acc_backpack' },
];

export class AvatarDemoScene extends Phaser.Scene {
  static readonly KEY = 'AvatarDemo';

  private avatars: AnimatedDogAvatar[] = [];
  private status: Phaser.GameObjects.Text | null = null;
  private anchorsMode = false;
  private editBreed: string = 'maltese';
  private editPose: PoseName = 'idle';
  private editAvatar: AnimatedDogAvatar | null = null;
  private anchorDots: Phaser.GameObjects.Arc[] = [];
  private anchorKeys: Array<'head' | 'neck' | 'back' | 'mouth'> = ['head', 'neck', 'back', 'mouth'];
  private anchorsData: Record<string, Record<string, Partial<AnchorPose>>> = {};
  private dragging: { key: 'head' | 'neck' | 'back' | 'mouth'; dot: Phaser.GameObjects.Arc } | null =
    null;
  private exportHint: Phaser.GameObjects.Text | null = null;

  constructor() {
    super(AvatarDemoScene.KEY);
  }

  preload(): void {
    this.load.image('ph_dog', '/assets/placeholder/dog.png');
    // maltese pełny zestaw
    for (const pose of ['idle', 'walkA', 'walkB', 'sit', 'jump', 'wave'] as const) {
      this.load.image(`dog_maltese_${pose}`, `/assets/real/maltese_${pose}.png`);
    }
    for (const b of DOG_BREED_IDS) {
      if (b === 'maltese') continue;
      this.load.image(`dog_${b}_idle`, `/assets/real/dog_${b}_idle.png`);
    }
    // ubranka realne (krótkie klucze) + mapowanie nie jest 1:1 z itemId — procedural OK
    for (const id of ['bandana', 'bow', 'crown', 'glasses', 'hat', 'sweater']) {
      this.load.image(`item_${id}`, `/assets/real/item_${id}.png`);
    }
    this.load.json('anchors', '/assets/real/anchors.json');
  }

  async create(): Promise<void> {
    this.cameras.main.setBackgroundColor(0xfff3e9);
    this.anchorsMode = this.isAnchorsQuery();

    try {
      const raw = this.cache.json.get('anchors');
      this.anchorsData = parseAnchorsFile(raw);
    } catch {
      this.anchorsData = parseAnchorsFile(buildDefaultAnchorsFile());
    }
    await preloadAnchors(this);

    this.add
      .text(GAME_WIDTH / 2, 28, this.anchorsMode ? 'Kalibrator kotwic (?anchors=1)' : 'Demo awatarów (?avatar=1)', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '26px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.status = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT - 28, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#6b5f7a',
      })
      .setOrigin(0.5);

    if (this.anchorsMode) {
      this.createAnchorsEditor();
    } else {
      this.createWalkDemo();
    }
  }

  update(_t: number, dtMs: number): void {
    const dt = dtMs / 1000;
    for (const a of this.avatars) a.update(dt);
    this.editAvatar?.update(dt);
  }

  private isAnchorsQuery(): boolean {
    try {
      return new URLSearchParams(window.location.search).get('anchors') === '1';
    } catch {
      return false;
    }
  }

  private createWalkDemo(): void {
    const cols = 3;
    const gapX = GAME_WIDTH / cols;
    const gapY = 280;
    const startY = 160;

    DOG_BREED_IDS.forEach((breed, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = gapX * col + gapX / 2;
      const y = startY + row * gapY;

      const av = new AnimatedDogAvatar({
        scene: this,
        x,
        y,
        breed,
        color: DOGS[breed].defaultColor,
        scale: 0.85,
        slots: DEMO_OUTFITS[i % DEMO_OUTFITS.length],
        anchorsData: this.anchorsData,
        depth: 10,
      });
      this.avatars.push(av);

      this.add
        .text(x, y + 70, DOGS[breed].namePl, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '18px',
          color: '#4a3f55',
          fontStyle: 'bold',
        })
        .setOrigin(0.5);

      // patrol lewo-prawo
      const left = x - 70;
      const right = x + 70;
      let dir = i % 2 === 0 ? 1 : -1;
      this.time.addEvent({
        delay: 16,
        loop: true,
        callback: () => {
          const px = av.container.x;
          if (px > right) dir = -1;
          if (px < left) dir = 1;
          av.setVelocity(dir * 55, 0);
        },
      });
    });

    // przyciski akcji na pierwszym (maltese)
    const actions: Array<{ label: string; a: 'sit' | 'jump' | 'wave' | 'eat' | 'happy' | 'sleep' }> = [
      { label: 'Siad', a: 'sit' },
      { label: 'Skok', a: 'jump' },
      { label: 'Pa pa', a: 'wave' },
      { label: 'Jedz', a: 'eat' },
      { label: 'Hurra', a: 'happy' },
      { label: 'Śpij', a: 'sleep' },
    ];
    actions.forEach((btn, i) => {
      const bx = 80 + i * 120;
      const by = GAME_HEIGHT - 70;
      const r = this.add
        .rectangle(bx, by, 100, 48, 0xffb6c1, 0.95)
        .setStrokeStyle(3, 0xffffff)
        .setInteractive({ useHandCursor: true });
      this.add
        .text(bx, by, btn.label, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '16px',
          color: '#4a3f55',
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
      r.on('pointerup', () => {
        const target = this.avatars[0];
        if (!target) return;
        target.stop();
        void target.playAction(btn.a).then(() => {
          this.status?.setText(`${DOGS.maltese.namePl}: ${btn.label} — gotowe`);
        });
      });
    });

    this.status?.setText('6 ras w ubrankach · tapnij akcję (Maltańczyk) · fallback gdy brak klatek');
  }

  private createAnchorsEditor(): void {
    this.editAvatar = new AnimatedDogAvatar({
      scene: this,
      x: GAME_WIDTH / 2,
      y: GAME_HEIGHT / 2 + 20,
      breed: this.editBreed,
      color: DOGS.maltese.defaultColor,
      scale: 1.4,
      slots: DEMO_OUTFITS[0],
      anchorsData: this.anchorsData,
      depth: 10,
    });

    this.exportHint = this.add
      .text(16, 60, 'Przeciągaj kotwice · E = eksport JSON do konsoli/schowka', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '15px',
        color: '#6b5f7a',
        wordWrap: { width: GAME_WIDTH - 32 },
      });

    this.buildBreedPoseButtons();
    this.rebuildAnchorDots();

    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (!this.dragging || !this.editAvatar) return;
      const localX = (p.worldX - this.editAvatar.container.x) / 1.4;
      const localY = (p.worldY - this.editAvatar.container.y) / 1.4;
      const pivot = 64;
      const ax = localX + pivot;
      const ay = localY + pivot;
      const breedMap = (this.anchorsData[this.editBreed] ??= {});
      const pose = resolveAnchors(this.anchorsData, this.editBreed, this.editPose);
      const next: AnchorPose = {
        ...pose,
        [this.dragging.key]: [Math.round(ax), Math.round(ay)],
      };
      breedMap[this.editPose] = next;
      this.dragging.dot.setPosition(p.worldX, p.worldY);
      this.editAvatar.setAnchorsData(this.anchorsData);
    });

    this.input.on('pointerup', () => {
      this.dragging = null;
    });

    this.input.keyboard?.on('keydown-E', () => {
      const json = JSON.stringify(this.anchorsData, null, 2);
      // eslint-disable-next-line no-console
      console.log('[anchors export]\n' + json);
      try {
        void navigator.clipboard?.writeText(json);
        this.status?.setText('Wyeksportowano kotwice do schowka i konsoli');
      } catch {
        this.status?.setText('Wyeksportowano kotwice do konsoli (schowek niedostępny)');
      }
    });

    // cykl póz strzałkami
    this.input.keyboard?.on('keydown-RIGHT', () => this.shiftPose(1));
    this.input.keyboard?.on('keydown-LEFT', () => this.shiftPose(-1));

    this.status?.setText(`Rasa: ${this.editBreed} · poza: ${this.editPose} · strzałki = poza`);
  }

  private buildBreedPoseButtons(): void {
    DOG_BREED_IDS.forEach((b, i) => {
      const x = 70 + i * 100;
      const r = this.add
        .rectangle(x, 100, 90, 36, 0xb8e6d0, 0.9)
        .setStrokeStyle(2, 0xffffff)
        .setInteractive({ useHandCursor: true });
      this.add
        .text(x, 100, DOGS[b].namePl.slice(0, 8), {
          fontSize: '13px',
          color: '#4a3f55',
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
      r.on('pointerup', () => {
        this.editBreed = b;
        this.editAvatar?.setBreed(b, DOGS[b].defaultColor);
        this.rebuildAnchorDots();
        this.status?.setText(`Rasa: ${b} · poza: ${this.editPose}`);
      });
    });
  }

  private shiftPose(dir: number): void {
    const idx = ALL_POSES.indexOf(this.editPose);
    const next = ALL_POSES[(idx + dir + ALL_POSES.length) % ALL_POSES.length]!;
    this.editPose = next;
    // pokaż pozę przez playAction lub setPose
    if (next === 'idle' || next === 'walkA' || next === 'walkB') {
      this.editAvatar?.playIdle();
      if (next !== 'idle') {
        this.editAvatar?.setVelocity(next === 'walkA' ? 40 : -40, 0);
      } else {
        this.editAvatar?.stop();
      }
    } else if (
      next === 'sit' ||
      next === 'jump' ||
      next === 'wave' ||
      next === 'eat' ||
      next === 'swim' ||
      next === 'sleep' ||
      next === 'happy'
    ) {
      this.editAvatar?.stop();
      void this.editAvatar?.playAction(next);
    }
    this.rebuildAnchorDots();
    this.status?.setText(`Rasa: ${this.editBreed} · poza: ${this.editPose}`);
  }

  private rebuildAnchorDots(): void {
    for (const d of this.anchorDots) d.destroy();
    this.anchorDots = [];
    if (!this.editAvatar) return;

    const pose = resolveAnchors(this.anchorsData, this.editBreed, this.editPose);
    const colors: Record<string, number> = {
      head: 0xff6b6b,
      neck: 0xffd700,
      back: 0x7ec8e3,
      mouth: 0xb8e6d0,
    };
    const scale = 1.4;
    for (const key of this.anchorKeys) {
      const [ax, ay] = pose[key];
      const lx = ax - pose.flipPivot;
      const ly = ay - pose.flipPivot;
      const wx = this.editAvatar.container.x + lx * scale;
      const wy = this.editAvatar.container.y + ly * scale;
      const dot = this.add
        .circle(wx, wy, 10, colors[key], 0.95)
        .setStrokeStyle(2, 0xffffff)
        .setDepth(50)
        .setInteractive({ draggable: false, useHandCursor: true });
      dot.setData('key', key);
      this.add
        .text(wx, wy - 16, key, {
          fontSize: '11px',
          color: '#4a3f55',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(51);
      dot.on('pointerdown', () => {
        this.dragging = { key, dot };
      });
      this.anchorDots.push(dot);
    }
  }
}
